/**
 * The title screen, d2o: a fire (fire.ts, drawn by a shader over the whole window) that lights the
 * name above it and the one button below it, and one line under that which asks for the game and
 * then answers what the files are. Shown before any game art
 * exists, so it is our own design, and it says as little as it can.
 * Accepts disc images, Blizzard's installer tomes (Installer Tome.mpq, read in place), loose MPQs,
 * the patch executables (D2Patch_114d.exe, LODPatch_109d.exe) and whole folders (the installer
 * folders, a Diablo II install with its patch_d2.mpq and Game.exe), shows what was found, which version it adds up to and what is missing, then streams
 * the archives into browser storage.
 */
import {
  type ArchiveName,
  archivesInIso,
  archivesInTome,
  BlobSource,
  describeMount,
  type IgnoredFile,
  mountArchives,
  type NamedSource,
  type PatchTarget,
  REQUIRED_ARCHIVES,
} from '@d2/formats';
import { filesFromDrop, filesFromInput, type PickedFile, pickGameFiles } from './gather';
import './import.css';
import { startFire } from './fire';
import { forgetArchives, requestPersistence, StorageFullError, storageEstimate, storeArchive } from './storage';
import { quotaMessage } from './support';
import { loadingHtml, loadingPanel } from './loading';

const LABELS: Record<ArchiveName, string> = {
  'd2patch_114d.exe': 'Patch 1.14d',
  'lod_109d.exe': 'Patch 1.09d',
  'patch_d2.mpq': 'Installed patch',
  'd2data.mpq': 'Game data',
  'd2char.mpq': 'Characters',
  'd2sfx.mpq': 'Sound effects',
  'd2speech.mpq': 'Speech',
  'd2music.mpq': 'Music',
  'd2exp.mpq': 'Lord of Destruction',
  'd2xmusic.mpq': 'LoD music',
  'd2xtalk.mpq': 'LoD speech',
  'game.exe': 'Game version',
};

const STORED = 'browser storage';
/** What the title screen asks for, under its button. */
const ASK = 'Diablo II: Lord of Destruction 1.14';
const fmtMB = (n: number) => `${(n / (1 << 20)).toFixed(0)} MB`;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);

export function showImportScreen(root: HTMLElement, opts: { devDiscs: boolean; preloaded?: NamedSource[]; patch?: PatchTarget; notice?: string; mobile?: boolean }): Promise<'stored' | 'dev'> {
  return new Promise((resolve) => {
    const found = new Map<ArchiveName, NamedSource>();
    const ignored = new Map<string, IgnoredFile>();
    for (const s of opts.preloaded ?? []) found.set(s.name, s);

    root.innerHTML = `
      <div class="import title-page" style="--lit: 0">
        <canvas class="fire" aria-hidden="true"></canvas>
        <main class="card">
          <h1 class="mark">d2o</h1>
          <div class="hearth"></div>
          <div class="pick">
            <button class="act folder-btn">Play</button>
            <button class="act go" hidden>Enter</button>
            <p class="status" role="status" aria-live="polite">${ASK}</p>
          </div>
          ${loadingHtml()}
        </main>
        <input class="folder" type="file" webkitdirectory hidden />
        <dialog class="get-files mobile-note" aria-labelledby="mobile-note-title"><div class="sheet">
          <h2 id="mobile-note-title">d2o is not available on mobile (yet)</h2>
          <p class="lede">Please try again on your computer, in Google Chrome, Microsoft Edge or Safari.</p>
          <button class="act ok">OK</button>
          <p class="fine">Private and incognito windows won't work.</p>
        </div></dialog>
        <dialog class="get-files" aria-labelledby="get-files-title"><div class="sheet">
          <button class="close" aria-label="Close">×</button>
          <h2 id="get-files-title">To play, add your Diablo II files</h2>
          <p class="lede">The game runs on the files from your own copy of Diablo II and Lord of Destruction.</p>
          <ol>
            <li>Go to <a href="https://account.battle.net/" target="_blank" rel="noopener">account.battle.net</a> and sign in.</li>
            <li>Open <i>Games &amp; Subscriptions</i>. Under <i>Classic Games</i>, download Diablo II and Lord of Destruction. If they aren't there, add your CD keys first.</li>
            <li>Put both downloads in one folder.</li>
            <li>Click <i>Choose Folder</i> below and select that folder.</li>
          </ol>
          <button class="act choose">Choose Folder</button>
          <p class="fine">Your game files stay on this computer. <a href="help.html" target="_blank" rel="noopener">Need help getting the files?</a></p>
        </div></dialog>
        <footer class="legal"><p class="own">You must own Diablo II to play.</p>This is an unofficial fan project, not affiliated with Blizzard Entertainment.<br />
          Diablo is a trademark of Blizzard Entertainment, Inc. <a href="about.html" target="_blank" rel="noopener">Learn More</a>${opts.preloaded?.length ? ' · <button class="link forget">Forget files</button>' : ''}${opts.devDiscs ? ' · <button class="link dev">Developer discs</button>' : ''}</footer>
      </div>`;

    const $ = <T extends HTMLElement>(s: string) => root.querySelector(s) as T;
    const page = $<HTMLDivElement>('.import');
    const card = $<HTMLElement>('.card');
    const folder = $<HTMLInputElement>('input.folder');
    const pick = $<HTMLButtonElement>('button.folder-btn');
    const go = $<HTMLButtonElement>('button.go');
    const status = $<HTMLParagraphElement>('.status');
    // The fire lights the page: its brightness drives --lit (the name, the button's edge, the floor).
    const fire = startFire($<HTMLCanvasElement>('canvas.fire'), $('.hearth'), matchMedia('(prefers-reduced-motion: reduce)').matches, (b) =>
      page.style.setProperty('--lit', b.toFixed(3)),
    );
    if (!fire) page.style.setProperty('--lit', '1');
    // After the entrance, buttons that appear later materialise without its delay (import.css).
    setTimeout(() => page.classList.add('settled'), 3000);
    // iOS Safari applies :active (the buttons' press) only where a touch listener exists.
    page.addEventListener('touchstart', () => {}, { passive: true });
    // When the line under the button says something new, the words come up out of a slight blur
    // (not during the entrance, which brings the line in itself).
    let said = status.textContent;
    new MutationObserver(() => {
      if (status.textContent === said) return;
      said = status.textContent;
      if (page.classList.contains('settled')) status.animate({ opacity: [0, 1], filter: ['blur(2px)', 'blur(0)'] }, { duration: 220, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
    }).observe(status, { childList: true, characterData: true });
    // The fire flares once the files add up, and stirs while the pointer or focus rests on the button.
    let ready = false;
    let stirred = false;
    const tend = () => fire?.setHeat((ready ? 1.2 : 1) + (stirred ? 0.1 : 0));
    const stir = (on: boolean) => {
      stirred = on;
      tend();
    };
    const dialog = $<HTMLDialogElement>('dialog.get-files:not(.mobile-note)');

    /** One line: what the files add up to, what is missing, or what is wrong. */
    let generation = 0;
    const refresh = async () => {
      const missing = REQUIRED_ARCHIVES.filter((n) => !found.has(n));
      ready = missing.length === 0;
      go.hidden = !ready;
      go.disabled = !ready;
      pick.hidden = ready;
      tend();
      if (!found.size) {
        status.classList.remove('error');
        status.textContent = ASK;
        return;
      }
      const g = ++generation;
      try {
        const r = await mountArchives([...found.values()], { patch: opts.patch ?? '1.14d', ignored: [...ignored.values()] });
        if (g !== generation) return;
        const d = describeMount(r, LABELS);
        status.classList.toggle('error', d.problems.length > 0);
        if (!ready) status.textContent = `Missing: ${missing.map((n) => LABELS[n]).join(', ')}.`;
        else if (d.problems.length) status.textContent = `${d.problems[0]} ${d.advice[0] ?? ''}`.trim();
        else status.textContent = d.title;
      } catch (e) {
        if (g !== generation) return;
        status.textContent = `Could not read your files: ${(e as Error).message}`;
        status.classList.add('error');
      }
    };
    void refresh().then(() => {
      if (opts.notice && !found.size) {
        status.textContent = opts.notice;
        status.classList.add('error');
      }
    });

    const addFiles = async (files: PickedFile[]) => {
      status.classList.remove('error');
      const picked = pickGameFiles(files);
      for (const f of picked.ignored) ignored.set(f.name, f);
      // Newly added files replace earlier ones of the same name; within one drop the shallowest wins.
      const added = new Set<ArchiveName>();
      for (const a of picked.archives) {
        if (added.has(a.name)) continue;
        added.add(a.name);
        found.set(a.name, { name: a.name, source: new BlobSource(a.file), origin: a.origin });
      }
      for (const f of picked.isos) {
        try {
          status.textContent = 'Reading…';
          const extra: IgnoredFile[] = [];
          for (const s of await archivesInIso(new BlobSource(f.file), f.path, extra)) {
            const prev = found.get(s.name);
            if (!prev || prev.origin === STORED) found.set(s.name, s);
          }
          for (const x of extra) ignored.set(x.name, x);
          status.textContent = '';
        } catch (e) {
          status.textContent = `${f.path}: ${(e as Error).message}`;
          status.classList.add('error');
        }
      }
      for (const f of picked.tomes) {
        try {
          status.textContent = 'Reading…';
          const extra: IgnoredFile[] = [];
          // The archives inside are slices of the dropped file: nothing is read whole.
          const tome = await archivesInTome(new BlobSource(f.file), f.path, extra);
          for (const s of tome.sources) {
            const prev = found.get(s.name);
            if (!prev || prev.origin === STORED) found.set(s.name, s);
          }
          for (const x of extra) ignored.set(x.name, x);
          status.textContent = '';
        } catch (e) {
          status.textContent = `${f.path}: ${(e as Error).message}`;
          status.classList.add('error');
        }
      }
      if (!picked.archives.length && !picked.isos.length && !picked.tomes.length && !picked.ignored.length && files.length) {
        status.textContent = 'No Diablo II files there.';
        status.classList.add('error');
      }
      await refresh();
    };

    for (const b of [pick, go]) {
      b.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && stir(true));
      b.addEventListener('pointerleave', () => stir(false));
      b.addEventListener('focus', () => stir(true));
      b.addEventListener('blur', () => stir(false));
    }
    folder.addEventListener('change', () => void addFiles(filesFromInput(folder)));
    // Play first says where the files come from; Choose Folder opens the picker (same gesture).
    // On a phone or tablet it says d2o needs a computer instead.
    const note = $<HTMLDialogElement>('dialog.mobile-note');
    pick.addEventListener('click', () => (opts.mobile ? note : dialog).showModal());
    $<HTMLButtonElement>('dialog.mobile-note .ok').addEventListener('click', () => note.close());
    note.addEventListener('click', (e) => {
      if (e.target === note) note.close();
    });
    $<HTMLButtonElement>('dialog .choose').addEventListener('click', () => {
      dialog.close();
      folder.click();
    });
    $<HTMLButtonElement>('dialog .close').addEventListener('click', () => dialog.close());
    // A click on the dimmed page outside the box closes it.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    // The whole screen takes a dropped folder or files.
    let dragDepth = 0;
    page.addEventListener('dragenter', (e) => {
      e.preventDefault();
      if (dragDepth++ === 0) page.classList.add('over');
    });
    page.addEventListener('dragover', (e) => e.preventDefault());
    page.addEventListener('dragleave', () => {
      if (--dragDepth <= 0) {
        dragDepth = 0;
        page.classList.remove('over');
      }
    });
    page.addEventListener('drop', (e) => {
      e.preventDefault();
      dragDepth = 0;
      page.classList.remove('over');
      if (dialog.open) dialog.close();
      if (card.classList.contains('busy')) return;
      if (e.dataTransfer) void filesFromDrop(e.dataTransfer).then(addFiles);
    });
    $<HTMLButtonElement>('button.dev')?.addEventListener('click', () => {
      fire?.stop();
      root.innerHTML = '';
      resolve('dev');
    });
    $<HTMLButtonElement>('button.forget')?.addEventListener('click', async () => {
      await forgetArchives();
      for (const [n, s] of found) if (s.origin === STORED) found.delete(n);
      $<HTMLButtonElement>('button.forget').remove();
      status.classList.remove('error');
      await refresh();
    });

    go.addEventListener('click', async () => {
      go.disabled = true;
      // Archives already in browser storage are not copied again.
      const list = [...found.values()].filter((s) => s.origin !== STORED);
      const total = list.reduce((a, s) => a + s.source.size, 0) || 1;
      let done = 0;
      let current = '';
      // Without FileSystemHandle.move the largest archive briefly exists twice (copied into place).
      const canMove = typeof FileSystemHandle !== 'undefined' && 'move' in FileSystemHandle.prototype;
      const needed = total + (canMove ? 0 : Math.max(0, ...list.map((s) => s.source.size)));
      try {
        void requestPersistence();
        const est = await storageEstimate().catch(() => null);
        if (est && est.quota > 0 && est.quota - est.usage < needed) {
          status.textContent = quotaMessage(needed, est.quota - est.usage);
          status.classList.add('error');
          go.disabled = false;
          return;
        }
        // From here on the card shows the loading stages and the controls.
        card.classList.add('busy');
        const loading = loadingPanel(card);
        if (list.length) loading.stage('Loading files…', 0);
        for (const s of list) {
          current = LABELS[s.name];
          await storeArchive(s, (w) => {
            loading.stage('Loading files…', ((done + w) / total) * 100);
            loading.el.querySelector('.stage')!.setAttribute('title', `${LABELS[s.name]}: ${fmtMB(done + w)} of ${fmtMB(total)}`);
          });
          done += s.source.size;
        }
        loading.stage('Opening your files…');
        resolve('stored');
      } catch (e) {
        const est = e instanceof StorageFullError ? await storageEstimate().catch(() => null) : null;
        const free = est ? est.quota - est.usage : 0;
        // The browser's allowance can exceed what the disk really has left (the usual case on Safari).
        const full = free >= needed ? `Your disk ran out of space while saving ${current}. Free up at least ${fmtMB(needed)} and try again.` : quotaMessage(needed, free);
        status.textContent = e instanceof StorageFullError ? full : `Could not load ${current || 'your files'}: ${(e as Error).message}`;
        status.classList.add('error');
        card.classList.remove('busy');
        go.disabled = false;
      }
    });
  });
}
